/* eslint-disable no-nested-ternary */
import { Form, Formik } from 'formik';
import Modal from '~/components/common/Modal';
import { AddZoneSchema } from '~/utils/schema/integration';
import { Typography, Stack, Box, Divider } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import themes from '~/themes';
import {
  useCreateHubZonesMutation,
  useUpdateHubZonesMutation,
} from '~/services/api/stores';
import { useLocation } from 'react-router-dom';
import { fromQueryString } from '~/utils/queryString';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Zone } from '~/models/stores';
import { alertParams, showAlert } from '~/components/common/Alert';
import Button from '~/components/common/Button';
import DeleteIcon from '~/assets/images/icons/delete-black.svg';
import DuplicateIcon from '~/assets/images/icons/duplicate.svg';
import { isEqual } from 'lodash';
import { DeliveryZoneSets } from '../../types';
import { DialogAddZoneForm } from './DrawAddZoneForm';

interface DialogAddZoneIProps {
  open: boolean;
  onClose: () => void;
  zoneSelected?: Zone;
  defaultCenter?: {
    lat: number;
    lng: number;
  };
}

export const FeeTypeElement = ({ type, open }) => (
  <>
    <Typography color={themes.color.violet900}>
      {type === 'fixed_amount' ? '$' : '%'}
    </Typography>
    {open ? (
      <KeyboardArrowDownIcon
        sx={{
          color: themes.color.violet900,
        }}
      />
    ) : (
      <KeyboardArrowUpIcon
        sx={{
          color: themes.color.violet900,
        }}
      />
    )}
  </>
);

export const DialogAddZone: React.FC<DialogAddZoneIProps> = ({
  open,
  onClose,
  zoneSelected,
  defaultCenter,
}) => {
  const location = useLocation();
  const search = fromQueryString(location.search);
  const isEdit = !!zoneSelected;
  const drawnZoneMapRef = useRef(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const [missCoordinateErr, setMissCoordinateErr] = useState('');
  const {
    mutate: createHubZonesMutation,
    isLoading: isLoadingCreateHubZonesMutation,
  } = useCreateHubZonesMutation({
    onSuccess: () => {
      onClose();
    },
  });

  const {
    mutate: updateHubZonesMutation,
    isLoading: isLoadingUpdateHubZonesMutation,
  } = useUpdateHubZonesMutation({
    onSuccess: () => {
      onClose();
    },
  });

  const onSubmit = (values: any) => {
    const {
      name,
      delivery_fee_type,
      long_lat,
      delivery_fee,
      delivery_minimum,
      enable,
    } = values;

    const isUpdating = isEdit && zoneSelected.id && !isDuplicate;

    const zoneCoordinates = isEdit
      ? drawnZoneMapRef.current.getPaths()
      : long_lat;

    if (!zoneCoordinates?.length) {
      return setMissCoordinateErr(
        'Please complete drawing your delivery zone.',
      );
    }

    setMissCoordinateErr('');

    const baseParams = {
      integration_id: search.id,
      name,
      enable,
      delivery_fee,
      delivery_fee_type,
      delivery_minimum,
      area_type: DeliveryZoneSets.DrawnZones,
      long_lat: zoneCoordinates,
    };

    const params = {
      action: isUpdating ? 'update' : 'create',
      parameters: isUpdating
        ? { ...baseParams, _id: zoneSelected.id }
        : baseParams,
    };

    return isUpdating
      ? updateHubZonesMutation(params)
      : createHubZonesMutation(params);
  };

  const confirmClose = () => {
    showAlert({
      ...alertParams.warnDark,
      title: 'Warning',
      description:
        'If you leave this page, unsaved changes will be lost.\nAre you sure you want to continue?',
      onOk: () => onClose(),
    });
  };

  const handleDelete = () => {
    showAlert({
      ...alertParams.warning,
      description:
        'Once deleted, this zone cannot be recovered.\nAre you sure you want to continue?',
      okText: 'Yes, Delete',
      cancelText: 'Cancel',
      onOk: () => {
        updateHubZonesMutation({
          action: 'delete',
          parameters: {
            _id: zoneSelected.id,
          },
        });
      },
    });
  };

  const handleClose = () => {
    const currentPaths = drawnZoneMapRef.current.getPaths();
    const isEqualLatLng =
      !isEdit || isEqual(currentPaths, zoneSelected.long_lat);

    if (isFormDirty || !isEqualLatLng) {
      confirmClose();
    } else {
      onClose();
    }
  };

  const title = useMemo(() => {
    if (isDuplicate) return 'Duplicate Zone';
    if (isEdit && zoneSelected.id) return zoneSelected.name;
    return 'Add Zone';
  }, [isEdit, isDuplicate, zoneSelected]);

  return (
    <Modal
      title={title}
      open={open}
      onClose={() => handleClose()}
      disableCloseOutside
      PaperProps={{
        sx: {
          'maxWidth': 690,
          '.MuiDialogContent-root': {
            paddingBottom: '20px',
          },
        },
      }}
    >
      <Formik
        initialValues={
          isEdit
            ? zoneSelected
            : {
                name: '',
                delivery_fee: undefined,
                delivery_minimum: undefined,
                delivery_fee_type: 'fixed_amount',
                long_lat: [],
                enable: true,
              }
        }
        onSubmit={onSubmit}
        validationSchema={AddZoneSchema}
        enableReinitialize
      >
        {({
          values: { delivery_fee_type, long_lat },
          setFieldValue,
          dirty,
          submitForm,
        }) => {
          useEffect(() => {
            setIsFormDirty(dirty);
          }, [dirty]);

          return (
            <Stack>
              <Form id='create-zone'>
                <DialogAddZoneForm
                  defaultCenter={defaultCenter}
                  delivery_fee_type={delivery_fee_type}
                  long_lat={long_lat}
                  setFieldValue={setFieldValue}
                  drawnZoneMapRef={drawnZoneMapRef}
                  missCoordinateErr={missCoordinateErr}
                />
              </Form>
              <Divider
                sx={{
                  mx: -2,
                  marginTop: 1.6,
                }}
              />
              <Stack
                pt={2}
                flexDirection='row'
                alignItems='center'
                justifyContent='space-between'
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: '8px',
                  }}
                >
                  {isEdit && !isDuplicate && (
                    <>
                      <Button
                        buttonType='text'
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                        onClick={handleDelete}
                      >
                        <img src={DeleteIcon} alt='' /> Delete Zone
                      </Button>
                      <Button
                        buttonType='text'
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                        onClick={() => {
                          setIsDuplicate(true);
                          setFieldValue(
                            'name',
                            `Duplicate ${zoneSelected.name || ''}`,
                          );
                        }}
                      >
                        <img src={DuplicateIcon} alt='' />
                        Duplicate Zone
                      </Button>
                    </>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '8px',
                  }}
                >
                  <Button
                    buttonType='text'
                    onClick={handleClose}
                    sx={{
                      minWidth: 'unset !important',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    sx={{
                      minWidth: 'unset !important',
                    }}
                    loading={
                      isLoadingCreateHubZonesMutation ||
                      isLoadingUpdateHubZonesMutation
                    }
                    onClick={submitForm}
                  >
                    Save
                  </Button>
                </Box>
              </Stack>
            </Stack>
          );
        }}
      </Formik>
    </Modal>
  );
};
